// GA4 dataLayer abstraction layer (LEG-1435)
// All event pushes must go through trackEvent() — never push to window.dataLayer directly.

function getCookie(name) {
  try {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  } catch (_) {
    return null;
  }
}

function setCookie(name, value, days) {
  try {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = name + '=' + encodeURIComponent(value) + '; expires=' + expires + '; path=/; SameSite=Lax';
  } catch (_) {}
}

function getStoredParam(key) {
  try {
    return localStorage.getItem('_om_' + key) || getCookie('_om_' + key);
  } catch (_) {
    return getCookie('_om_' + key);
  }
}

function storeParam(key, value) {
  try {
    localStorage.setItem('_om_' + key, value);
  } catch (_) {}
  setCookie('_om_' + key, value, 30);
}

function getStoredClickIds() {
  return {
    fbc:       getCookie('_fbc')        || getStoredParam('fbc')       || undefined,
    fbp:       getCookie('_fbp')        || getStoredParam('fbp')       || undefined,
    fbclid:    getStoredParam('fbclid') || undefined,
    gclid:     getStoredParam('gclid')  || undefined,
    msclkid:   getStoredParam('msclkid')|| undefined,
    ttclid:    getStoredParam('ttclid') || undefined,
    li_fat_id: getStoredParam('li_fat_id') || undefined,
    rdt_cid:   getStoredParam('rdt_cid')|| undefined
  };
}

function getCurrentUserData() {
  try {
    const c = window.currentCustomerData || {};
    const data = {
      email:        (c.email || '').toLowerCase().trim() || undefined,
      phone_number: formatE164(c.notification_phone)     || undefined,
      // Identity fields per Orbit's data layer spec (DEV-2448); populated
      // from the customer metadata partial when logged in.
      user_id:      c.id != null ? String(c.id) : undefined,
      first_name:   (c.first_name || '').trim().toLowerCase() || undefined,
      last_name:    (c.last_name || '').trim().toLowerCase() || undefined
    };
    return Object.fromEntries(Object.entries(data).filter(([, v]) => v != null));
  } catch (_) {
    return {};
  }
}

function formatE164(phone) {
  if (!phone) return undefined;
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) return '+1' + digits;
  if (digits.length === 11 && digits[0] === '1') return '+' + digits;
  return undefined;
}

// Captures click IDs from URL params on landing and persists them.
// Run once on every page load.
function captureClickIds() {
  try {
    const params = new URLSearchParams(window.location.search);
    const keys = ['fbclid', 'gclid', 'msclkid', 'ttclid', 'rdt_cid', 'li_fat_id'];
    keys.forEach(function(key) {
      const val = params.get(key);
      if (!val) return;
      storeParam(key, val);
      // Persist to backend for offline event rehydration (requires auth session)
      try {
        window.axios && window.axios.post('/api/click-id', { param: key, value: val }).catch(function() {});
      } catch (_) {}
    });
  } catch (_) {}
}

// Standard GA4 ecommerce fields — go nested under `ecommerce` key in GTM data layer.
// All other fields stay at the top level (custom parameters, attribution, etc.)
const GA4_ECOMMERCE_FIELDS = ['currency', 'value', 'items', 'coupon', 'transaction_id', 'tax', 'shipping', 'affiliation', 'payment_type'];

// Stape soft cutover (Orbit plan, 2026-07): every core event gets a twin whose
// name ends in `_stape`, consumed ONLY by the new Stape-served GTM container
// (GTM-M2QBDHML). Legacy containers never bind to _stape names, so the two
// stacks cannot cross-fire. Enabled server-side by the layout partial setting
// window.__stapeEnabled (STAPE_GTM_ENABLED env var); off means zero twins.
const STAPE_TWIN_EVENTS = {
  page_view:        'page_view_stape',
  view_item:        'view_item_stape',
  generate_lead:    'generate_lead_stape',
  sign_up:          'sign_up_stape',
  // Renamed from add_to_wishlist_stape (DEV-2732, Angela via Shaun): the
  // watch action reports under an auction-specific name. The plain
  // add_to_wishlist event above is unchanged for legacy containers.
  add_to_wishlist:  'watch_auction_stape',
  add_to_cart:      'add_to_cart_stape',
  begin_checkout:   'begin_checkout_stape',
  add_payment_info: 'add_payment_info_stape',
  first_bid:        'first_bid_stape',
  place_bid:        'place_bid_stape',
  purchase:         'purchase_stape'
};

function stapeEnabled() {
  return window.__stapeEnabled === true;
}

// Unique id on every _stape push so browser and server (CAPI) tags for the
// same action dedupe inside the new stack.
function newEventId() {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
  } catch (_) {}
  return 'evt-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
}

// Current auction close time (DEV-2754 / Orbit DEV-03). Set only on auction
// lot pages (from universal_variable.product.close_date at load, refreshed
// from bid responses' closeTime.utc, including popcorn extensions), so
// attaching it to every push on the page cannot leak onto unrelated pages.
let auctionCloseDatetime = null;

function setAuctionCloseDatetime(value) {
  try {
    if (!value) return;
    const iso = new Date(value).toISOString();
    if (iso === auctionCloseDatetime) return;
    auctionCloseDatetime = iso;
    // Page-level dataLayer state so GTM variables can read it outside events.
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ close_datetime: iso });
  } catch (_) {}
}

function initAuctionCloseDatetime() {
  try {
    const uv = window.universal_variable || {};
    if (uv.product && uv.product.close_date) {
      setAuctionCloseDatetime(uv.product.close_date);
    }
  } catch (_) {}
}

// Shared push body used by both the original event and its _stape twin.
// Ecommerce events (those containing `items`, `transaction_id`, or `payment_type`) are
// structured in the standard GTM + GA4 format: ecommerce fields go under the `ecommerce`
// key and a `{ ecommerce: null }` reset is pushed first. This prevents GTM custom JS
// variables from throwing when they access `ecommerce.<field>` and ecommerce is undefined.
function pushToDataLayer(eventName, data, userData, eventId) {
  const extras = eventId ? { event_id: eventId } : {};
  if (auctionCloseDatetime) extras.close_datetime = auctionCloseDatetime;
  const isEcommerceEvent = !!(data.items || data.transaction_id || data.payment_type);

  if (isEcommerceEvent) {
    window.dataLayer.push({ ecommerce: null });

    const ecommerce = {};
    const custom = {};
    Object.keys(data).forEach(function(key) {
      if (GA4_ECOMMERCE_FIELDS.indexOf(key) !== -1) {
        ecommerce[key] = data[key];
      } else {
        custom[key] = data[key];
      }
    });

    window.dataLayer.push(Object.assign({}, custom, extras, { event: eventName, ecommerce: ecommerce, user_data: userData }));
  } else {
    window.dataLayer.push(Object.assign({}, data, extras, { event: eventName, user_data: userData }));
  }
}

// Central event push. Always use this — never push directly to window.dataLayer.
// Fails silently if GTM/GA is blocked by an adblocker.
// Legacy behavior is unchanged: the original event push is byte-for-byte what
// it was before the Stape twin mechanism was added.
function trackEvent(eventName, eventData) {
  try {
    window.dataLayer = window.dataLayer || [];
    const clickIds = getStoredClickIds();
    const userData = Object.assign({}, getCurrentUserData(), clickIds);
    const data = eventData || {};

    pushToDataLayer(eventName, data, userData);

    const twinName = STAPE_TWIN_EVENTS[eventName];
    if (twinName && stapeEnabled()) {
      pushToDataLayer(twinName, data, userData, newEventId());
    }
  } catch (_) {}
}

// For core events www does not fire under a plain GA4 name today
// (add_to_cart, add_payment_info in the BIN flow): emit ONLY the _stape twin.
// Net-new events are suffixed rather than double-pushed so no un-suffixed
// name is introduced that a legacy container trigger could accidentally bind to.
function trackStapeEvent(eventName, eventData) {
  try {
    if (!stapeEnabled()) return;
    window.dataLayer = window.dataLayer || [];
    const clickIds = getStoredClickIds();
    const userData = Object.assign({}, getCurrentUserData(), clickIds);
    const twinName = STAPE_TWIN_EVENTS[eventName] || (eventName + '_stape');

    pushToDataLayer(twinName, eventData || {}, userData, newEventId());
  } catch (_) {}
}

export { trackEvent, trackStapeEvent, captureClickIds, getCookie, setCookie, getStoredParam, setAuctionCloseDatetime, initAuctionCloseDatetime };
export default trackEvent;
